import React, { useState } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { getUserRole, canAccessAdmin, ROLES, ROLE_DISPLAY } from '../utils/roles';
import { APP_VERSION } from '../version';
import { klikBiasa } from '../utils/klikBiasa';

/* ═══════════════════════════════════════════════════
   NewSidebar — Dark NavRail
   Exact same props interface as old Sidebar.jsx
   ═══════════════════════════════════════════════════ */


// Menu aktif guna aksen ungu brand (dulu kelabu-putih — sidebar nampak mati dan
// warna logo tidak bergema di mana-mana). Submenu sedia ada pun guna keluarga
// warna yang sama, jadi keseluruhan sidebar kini konsisten.
const ACTIVE_BG = 'rgba(99,102,241,0.16)';
const ACTIVE_TEXT = '#e0e7ff';
const ACTIVE_ICON = '#a5b4fc';

const NewSidebar = ({
    lockedSet, onSetLockChange, spaces = [], selectedSpaceId, onSelectSpace,
    isCollapsed, onToggleCollapse, user,
    storageUsed = 0, storageLimitBytes = 2 * 1024 * 1024 * 1024,
    isCleaningStorage = false, onCleanupStorage,
    hideProductLibrary = false,
    userTier = null,
    userPlan = null,
}) => {
    const userRole = getUserRole(auth.currentUser?.email);
    const showAdminLink = canAccessAdmin(userRole);
    const isPrivileged = canAccessAdmin(userRole);
    const isClient = userRole === ROLES.CLIENT;
    const isTrial = userTier === 'trial';
    const location = useLocation();
    const navigate = useNavigate();


    const [isAddingSpace, setIsAddingSpace] = useState(false);
    const [newSpaceName, setNewSpaceName] = useState('');
    const [expandedMenu, setExpandedMenu] = useState(
        location.pathname.startsWith('/affiliate') ? '/affiliate'
        : location.pathname.startsWith('/admin') ? '/admin-group'
        : null
    );

    const toggleSubMenu = (path) => {
        setExpandedMenu(expandedMenu === path ? null : path);
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate('/');
        } catch (err) {
            console.error('Logout error:', err);
        }
    };

    // ═══ Nav Items ═══
    // Disusun jadi 3 kumpulan, dipisah GARIS NIPIS (bukan label teks) supaya mata
    // dapat tempat berehat tanpa menambah perkataan pada sidebar:
    //   1) Kerja harian  2) Bisnes  3) Info / alat
    const navGroups = [
        [
            { path: '/dashboard', icon: 'dashboard', label: 'Dashboard' },
            { path: '/workspace', icon: 'auto_awesome', label: 'Workspace' },
            { path: '/products', icon: 'inventory_2', label: 'Products' },
            { path: '/flow-google', icon: 'play_circle', label: 'Flow Google' }, // Diletak tepat atas Marketplace (arahan user 2026-09-27); nama + alamat ditukar dari "Flow" /my-flow (arahan user 2026-09-29)
            { path: '/marketplace', icon: 'shopping_bag', label: 'Marketplace' }, // DIBUKA kepada SEMUA pengguna (arahan user 2026-08-21) — dulu admin-only sementara
            { path: '/prostudio', icon: 'bolt', label: 'PRO Studio' }, // PRATONTON semua user — rangka sahaja, semua tab SOON, non-admin baca-sahaja (warm-up sebelum release Premium Pro / Fighter Pro)
        ],
        [
            {
                path: '/affiliate',
                icon: 'handshake',
                label: 'Affiliates',
                subItems: [
                    { path: '/affiliate/dashboard', icon: 'donut_small', label: 'Dashboard' },
                    { path: '/affiliate/referrals', icon: 'group', label: 'Referrals' },
                    { path: '/affiliate/payouts', icon: 'account_balance_wallet', label: 'Payouts' },
                    { path: '/affiliate/resources', icon: 'book_4', label: 'Resources' },
                    { path: '/affiliate/settings', icon: 'settings', label: 'Settings' }
                ]
            },
            ...(userTier === 'trial' ? [{ path: '/pricing', icon: 'workspace_premium', label: 'Langgan Sekarang', isUpgradeCta: true }] : []),
            // Paid: link ke pricing supaya nampak kad Add-on FLOWNITY (trial dah ada CTA oren atas)
            ...(userTier === 'paid' && !isPrivileged ? [{ path: '/pricing', icon: 'payments', label: 'Pricing' }] : []),
            ...(userRole === ROLES.ADMIN ? [{ path: '/pricing', icon: 'payments', label: 'Harga' }] : []),
            ...(userRole === ROLES.ADMIN ? [{ path: '/pricing-baru', icon: 'sell', label: 'Pricing (Baru)' }] : []), // SEMENTARA — prototaip laman harga (admin sahaja), buang bila dah siap
        ],
        [
            { path: '/notes', icon: 'sticky_note_2', label: 'Notes' },
            ...(userRole === ROLES.ADMIN ? [{ path: '/rewrite', icon: 'edit_note', label: 'Rewrite' }] : []),
            ...(userRole === ROLES.ADMIN ? [{ path: '/ai-instructions', icon: 'neurology', label: 'AI Logic' }] : []),
            ...(showAdminLink ? [{
                path: '/admin-group',
                icon: 'shield_person',
                label: 'Admin',
                subItems: [
                    /* `path` = alamat tab pertama; `padanan` = awalan yang
                       menyalakan baris ini. Kedua-duanya perlu: tanpa
                       `padanan`, membuka tab KEDUA mana-mana halaman admin
                       akan memadamkan tanda aktif pada baris menunya. */
                    { path: '/admin/system/dashboard', padanan: '/admin/system', icon: 'admin_panel_settings', label: 'System' },
                    { path: '/admin/affiliate/overview', padanan: '/admin/affiliate', icon: 'currency_exchange', label: 'Affiliate' },
                    { path: '/admin/marketplace/products', padanan: '/admin/marketplace', icon: 'storefront', label: 'Marketplace' },
                    { path: '/admin/automation/ai16saat', padanan: '/admin/automation', icon: 'account_tree', label: 'Automation' },
                    { path: '/admin/script-versions', icon: 'history', label: 'Versi Skrip' },
                    { path: '/admin/announce', icon: 'campaign', label: 'Pengumuman' },
                    ...(userRole === ROLES.ADMIN ? [{ path: '/admin/partners', icon: 'handshake', label: 'Partners' }] : []),
                    { path: '/pipeline-test', icon: 'science', label: 'Test Lab' },
                    { path: '/android-test', icon: 'smartphone', label: 'Android Test' }, // POC WebUSB/ADB (admin sahaja, Fasa 1)
                ]
            }] : []),
        ],
    ].filter(group => group.length > 0);

    // Ratakan jadi satu senarai + selit penanda garis pemisah antara kumpulan.
    const navItems = navGroups.flatMap((group, gi) => (
        gi === 0 ? group : [{ divider: true, path: `__divider_${gi}` }, ...group]
    ));

    // ═══ Add Space Handler ═══
    const handleAddSpace = async () => {
        if (!newSpaceName.trim() || !auth.currentUser) return;
        const newSpace = {
            id: `space_${Date.now()}`,
            name: newSpaceName.trim(),
            icon: 'folder',
            color: ['indigo', 'emerald', 'amber', 'rose', 'violet'][spaces.length % 5]
        };
        const cleanSpaces = [...spaces, newSpace].map(({ productCount, ...rest }) => rest);
        try {
            const docRef = doc(db, 'users', auth.currentUser.uid);
            await setDoc(docRef, { spaces: cleanSpaces }, { merge: true });
        } catch (err) {
            console.error('Error adding space:', err);
        }
        setNewSpaceName('');
        setIsAddingSpace(false);
        if (onSelectSpace) onSelectSpace(newSpace.id);
    };

    // ═══ User Display ═══
    const userName = user?.displayName || user?.email?.split('@')[0] || 'User';
    const userInitial = userName.charAt(0).toUpperCase();
    const userPhoto = user?.photoURL;
    // ── Plan label (baris kedua bawah nama) ──
    const isFighter = userTier === 'paid' && userPlan === 'yearly'; // users.plan 'yearly' = Fighter (sama pola PricingSection)
    const planLabel = isPrivileged
        ? (userRole === ROLES.ADMIN ? 'Admin' : 'Moderator')
        : isFighter ? 'Fighter'
        : userTier === 'paid' ? 'Premium'
        : userTier === 'trial' ? 'Free Trial'
        : 'Free';
    const planColor = isPrivileged ? '#818cf8'
        : isFighter ? '#f97316'
        : userTier === 'paid' ? '#f59e0b'
        : userTier === 'trial' ? '#38bdf8'
        : '#71717a';

    // ═══ Storage Helpers ═══
    const storageRatio = storageUsed / storageLimitBytes;
    const storageColor = storageRatio > 0.9 ? '#ef4444' : storageRatio > 0.7 ? '#f59e0b' : '#22c55e';
    const storageText = storageUsed < 1024 * 1024 * 1024
        ? `${(storageUsed / 1024 / 1024).toFixed(0)} MB`
        : `${(storageUsed / 1024 / 1024 / 1024).toFixed(1)} GB`;
    const storageLimitText = `${(storageLimitBytes / 1024 / 1024 / 1024).toFixed(0)} GB`;
    // Baris storage kini SATU baris — had penuh ("/ 2 GB") dipindah ke tooltip.
    // Angka + butang hanya bertukar warna bila ruang mula penuh; selain itu kekal senyap.
    const storageAlert = storageRatio > 0.7;
    const storageSizeColor = storageAlert ? storageColor : '#52525b';

    // ═══ Active Check Helpers ═══
    const isPathActive = (path) => {
        if (path === '/') return location.pathname === '/';
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const isGroupActive = (item) => {
        if (isPathActive(item.path)) return true;
        if (item.subItems) return item.subItems.some(sub => isPathActive(sub.path));
        return false;
    };

    /* ═══════════════════════════════════
       RENDER
       ═══════════════════════════════════ */
    return (
        <aside style={{
            ...S.container,
            width: isCollapsed ? 80 : 260,
        }}>
            {/* ─── Logo ─── */}
            <div
                style={{ ...S.logoArea, justifyContent: isCollapsed ? 'center' : 'flex-start', padding: isCollapsed ? '24px 0 16px' : '24px 24px 16px' }}
                onClick={() => navigate('/dashboard')}
            >
                <div style={S.logoIcon}>
                    <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#fff', fontVariationSettings: "'FILL' 1" }}>bolt</span>
                </div>
                {!isCollapsed && (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={S.logoText}>SESAAT</span>
                            <span style={S.betaBadge}>BETA</span>
                        </div>
                        <span style={{ flex: 1 }} />
                        {/* Collapse — ikon kecil di hujung header (dulu satu baris penuh
                            dalam senarai menu, mudah disangka menu). */}
                        <button
                            onClick={(e) => { e.stopPropagation(); if (onToggleCollapse) onToggleCollapse(); }}
                            title="Collapse"
                            aria-label="Collapse sidebar"
                            style={S.collapseBtn}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#e4e4e7'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#52525b'; }}
                        >
                            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>keyboard_double_arrow_left</span>
                        </button>
                    </>
                )}
            </div>

            {/* ─── Expand Toggle — hanya masa kuncup (kalau tidak, tiada jalan buka semula) ─── */}
            {isCollapsed && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
                    <button
                        onClick={onToggleCollapse}
                        title="Expand"
                        aria-label="Expand sidebar"
                        style={S.collapseBtn}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#e4e4e7'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#52525b'; }}
                    >
                        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>keyboard_double_arrow_right</span>
                    </button>
                </div>
            )}

            {/* ─── Extension — kad menonjol, sengaja di ATAS SEKALI ───
                 Gaya berbeza daripada baris nav biasa (kotak bertona indigo + versi)
                 supaya pelanggan mudah jumpa tempat muat turun/kemas kini extension. */}
            <div style={{ padding: '0 12px', marginBottom: 10 }}>
                <Link
                    to="/extension"
                    title={isCollapsed ? `Extension — install v${APP_VERSION}` : undefined}
                    style={{
                        ...S.extCard,
                        justifyContent: isCollapsed ? 'center' : 'flex-start',
                        padding: isCollapsed ? '10px 0' : '10px 12px',
                        background: isPathActive('/extension') ? 'rgba(99,102,241,0.26)' : 'rgba(99,102,241,0.10)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(99,102,241,0.22)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = isPathActive('/extension') ? 'rgba(99,102,241,0.26)' : 'rgba(99,102,241,0.10)'; }}
                >
                    <div style={S.extIconBox}>
                        <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#ffffff', fontVariationSettings: "'FILL' 1" }}>extension</span>
                    </div>
                    {!isCollapsed && (
                        <>
                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: '#e0e7ff', lineHeight: 1.3 }}>Extension</span>
                                <span style={{ fontSize: 10, fontWeight: 600, color: '#a5b4fc', lineHeight: 1.3 }}>Install · v{APP_VERSION}</span>
                            </div>
                            <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#818cf8', flexShrink: 0 }}>download</span>
                        </>
                    )}
                </Link>
            </div>

            {/* ─── Navigation ─── */}
            <nav style={S.navSection}>
                {navItems.map((item) => {
                    // ═══ Garis pemisah antara kumpulan ═══
                    if (item.divider) {
                        return (
                            <div
                                key={item.path}
                                style={{ height: 1, background: 'rgba(255,255,255,0.07)', margin: isCollapsed ? '7px 8px' : '7px 12px' }}
                            />
                        );
                    }

                    // ═══ Submenu Group ═══
                    if (item.subItems) {
                        const isActive = isGroupActive(item);
                        const isExpanded = expandedMenu === item.path;

                        return (
                            <div key={item.path} style={{ display: 'flex', flexDirection: 'column' }}>
                                <button
                                    onClick={() => {
                                        if (isCollapsed && onToggleCollapse) onToggleCollapse();
                                        toggleSubMenu(item.path);
                                    }}
                                    title={isCollapsed ? item.label : undefined}
                                    style={{
                                        ...S.navItem,
                                        justifyContent: isCollapsed ? 'center' : 'flex-start',
                                        padding: isCollapsed ? '0' : '0 24px',
                                        background: isActive ? ACTIVE_BG : 'transparent',
                                    }}
                                >
                                    {isActive && <div style={S.activeIndicator} />}
                                    <span className="material-symbols-outlined" style={{
                                        fontSize: 20,
                                        color: isActive ? ACTIVE_ICON : '#71717a',
                                        flexShrink: 0,
                                    }}>{item.icon}</span>
                                    {!isCollapsed && (
                                        <>
                                            <span style={{
                                                flex: 1, fontSize: 13, fontWeight: 500,
                                                color: isActive ? ACTIVE_TEXT : '#a1a1aa',
                                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                            }}>{item.label}</span>
                                            <span className="material-symbols-outlined" style={{
                                                fontSize: 16,
                                                color: '#52525b',
                                                transition: 'transform 0.2s',
                                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                            }}>expand_more</span>
                                        </>
                                    )}
                                </button>

                                {/* Submenu Items */}
                                <div style={{
                                    overflow: 'hidden',
                                    maxHeight: isExpanded && !isCollapsed ? 300 : 0,
                                    opacity: isExpanded && !isCollapsed ? 1 : 0,
                                    transition: 'all 0.3s ease-in-out',
                                    marginTop: isExpanded && !isCollapsed ? 4 : 0,
                                }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingLeft: 24, marginLeft: 16, borderLeft: '2px solid rgba(255,255,255,0.06)' }}>
                                        {item.subItems.map(sub => {
                                            /* Padanan awalan bila `padanan` diberi — kalau tidak,
                                               hanya tab PERTAMA sahaja menyalakan baris menunya. */
                                            const subActive = sub.padanan
                                                ? (location.pathname === sub.padanan || location.pathname.startsWith(sub.padanan + '/'))
                                                : location.pathname === sub.path;
                                            return (
                                                <Link
                                                    key={sub.path}
                                                    to={sub.path}
                                                    style={{
                                                        ...S.subItem,
                                                        background: subActive ? 'rgba(99,102,241,0.12)' : 'transparent',
                                                        color: subActive ? '#818cf8' : '#71717a',
                                                        textDecoration: 'none',
                                                    }}
                                                >
                                                    <span className="material-symbols-outlined" style={{ fontSize: 16, flexShrink: 0 }}>{sub.icon}</span>
                                                    <span style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub.label}</span>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    // ═══ Regular Nav Item ═══
                    const isActive = isPathActive(item.path);
                    const isLocked = item.locked;
                    const isCta = item.isUpgradeCta;
                    return (
                        <Link
                            key={item.path}
                            to={item.disabled ? '#' : item.path}
                            title={isCollapsed ? (isLocked ? `${item.label} (Terkunci)` : item.label) : undefined}
                            style={{
                                ...S.navItem,
                                justifyContent: isCollapsed ? 'center' : 'flex-start',
                                padding: isCollapsed ? '0' : '0 24px',
                                background: isCta
                                    ? 'linear-gradient(135deg, #f59e0b, #f97316)'
                                    : (isActive ? ACTIVE_BG : 'transparent'),
                                opacity: isLocked ? 0.4 : 1,
                                textDecoration: 'none',
                                margin: isCta ? '6px 0' : 0,
                                boxShadow: isCta ? '0 4px 14px rgba(245,158,11,0.35)' : 'none',
                            }}
                        >
                            {isActive && !isCta && <div style={S.activeIndicator} />}
                            <span className="material-symbols-outlined" style={{
                                fontSize: 20,
                                color: isCta ? '#ffffff' : (isActive ? ACTIVE_ICON : (isLocked ? '#3f3f46' : '#71717a')),
                                flexShrink: 0,
                                fontVariationSettings: isCta ? "'FILL' 1" : undefined,
                            }}>{item.icon}</span>
                            {!isCollapsed && (
                                <span style={{
                                    flex: 1, fontSize: 13, fontWeight: isCta ? 700 : 500,
                                    color: isCta ? '#ffffff' : (isActive ? ACTIVE_TEXT : (isLocked ? '#3f3f46' : '#a1a1aa')),
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    whiteSpace: 'nowrap', overflow: 'hidden',
                                }}>
                                    {item.label}
                                    {isLocked && (
                                        <span className="material-symbols-outlined" style={{ fontSize: 13, color: '#3f3f46', fontVariationSettings: "'FILL' 1" }}>lock</span>
                                    )}
                                    {item.dot && (
                                        <span
                                            title="Ada kemas kini baharu"
                                            style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }}
                                        />
                                    )}
                                </span>
                            )}
                            {isCollapsed && item.dot && (
                                <span
                                    title="Ada kemas kini baharu"
                                    style={{ position: 'absolute', top: 8, right: 12, width: 7, height: 7, borderRadius: '50%', background: '#ef4444' }}
                                />
                            )}
                            {isCollapsed && isLocked && (
                                <span className="material-symbols-outlined" style={{
                                    position: 'absolute', top: 2, right: 6, fontSize: 9, color: '#3f3f46',
                                    fontVariationSettings: "'FILL' 1",
                                }}>lock</span>
                            )}
                        </Link>
                    );
                })}
            </nav>

            {/* ─── Spacer — push storage to bottom ─── */}
            <div style={{ flex: 1 }} />

            {/* ─── Divider + Spaces Section (Admin only, only if there are non-default spaces) ─── */}
            {isPrivileged && spaces.filter(s => s.id !== 'default').length > 0 && <div style={{ ...S.divider, margin: isCollapsed ? '8px 12px' : '8px 24px' }} />}

            {/* ─── Spaces Section ─── */}
            {isPrivileged && spaces.filter(s => s.id !== 'default').length > 0 && <div style={S.spacesSection}>
                {!isCollapsed && (
                    <div style={S.spacesHeader}>
                        <span style={S.spacesTitle}>SPACE</span>
                    </div>
                )}

                {/* Space List */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {spaces.filter(s => s.id !== 'default').map((space) => {
                        const isSpaceActive = location.pathname === `/space/${encodeURIComponent(space.name)}`;
                        return (
                            <a
                                key={space.id}
                                /* Alamat SEBENAR, bukan "#": barulah klik tengah /
                                   Ctrl+klik / "Open link in new tab" membuka space ini,
                                   bukan tab kosong. */
                                href={`/space/${encodeURIComponent(space.name)}`}
                                title={isCollapsed ? space.name : undefined}
                                onClick={(e) => {
                                    if (!klikBiasa(e)) return;   // biar browser buka tab baharu
                                    e.preventDefault();
                                    navigate(`/space/${encodeURIComponent(space.name)}`);
                                    if (onSelectSpace) onSelectSpace(space.id);
                                }}
                                style={{
                                    ...S.spaceItem,
                                    justifyContent: isCollapsed ? 'center' : 'space-between',
                                    padding: isCollapsed ? '8px 0' : '8px 12px',
                                    background: isSpaceActive ? 'rgba(99,102,241,0.1)' : 'transparent',
                                    borderColor: isSpaceActive ? 'rgba(99,102,241,0.25)' : 'transparent',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
                                    <div style={{
                                        width: 32, height: 32, borderRadius: 8,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                        background: isSpaceActive ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.06)',
                                    }}>
                                        <span className="material-symbols-outlined" style={{
                                            fontSize: 18,
                                            color: isSpaceActive ? '#818cf8' : '#71717a',
                                        }}>{space.icon || 'folder'}</span>
                                    </div>
                                    {!isCollapsed && (
                                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                            <span style={{
                                                fontSize: 13, fontWeight: 600,
                                                color: isSpaceActive ? '#e4e4e7' : '#a1a1aa',
                                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                            }}>{space.name}</span>
                                            <span style={{ fontSize: 10, color: '#52525b' }}>{space.productCount || 0} Products</span>
                                        </div>
                                    )}
                                </div>
                                {!isCollapsed && (
                                    <button
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/space-settings/${space.id}`); }}
                                        style={S.spaceSettingsBtn}
                                        className="space-settings-btn"
                                        title="Space Settings"
                                    >
                                        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>settings</span>
                                    </button>
                                )}
                            </a>
                        );
                    })}
                </div>
            </div>}

            {/* ─── Storage — SATU baris: awan · bar · saiz · butang Optimize (kanan) ───
                 Butang kekal di tempat sama; bila ruang mula penuh (>70%) ia bertukar
                 warna supaya menarik perhatian, bukan berpindah tempat. */}
            <div style={{ ...S.storageSection, padding: isCollapsed ? '12px 8px' : '11px 24px' }}>
                {isCollapsed ? (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <button
                            onClick={() => { if (onCleanupStorage) onCleanupStorage(); }}
                            disabled={isCleaningStorage}
                            title={`Storage: ${storageText} / ${storageLimitText} — klik untuk Optimize`}
                            aria-label="Optimize storage"
                            style={{
                                ...S.storageIconBtn,
                                cursor: isCleaningStorage ? 'not-allowed' : 'pointer',
                                opacity: isCleaningStorage ? 0.5 : 1,
                            }}
                        >
                            <span className="material-symbols-outlined" style={{
                                fontSize: 20, color: storageColor, fontVariationSettings: "'FILL' 1",
                                ...(isCleaningStorage ? { animation: 'spin 1s linear infinite' } : {}),
                            }}>{isCleaningStorage ? 'refresh' : 'cloud'}</span>
                        </button>
                    </div>
                ) : (
                    <>
                        <div
                            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                            title={`Storage: ${storageText} / ${storageLimitText}`}
                        >
                            <span className="material-symbols-outlined" style={{
                                fontSize: 15, color: storageColor, fontVariationSettings: "'FILL' 1", flexShrink: 0,
                            }}>cloud</span>

                            {/* Progress Bar */}
                            <div style={{ ...S.storageBarTrack, flex: 1, minWidth: 28 }}>
                                <div style={{
                                    ...S.storageBarFill,
                                    width: `${Math.min(storageRatio * 100, 100)}%`,
                                    background: storageColor,
                                }} />
                            </div>

                            <span style={{
                                fontSize: 10, fontWeight: 700, color: storageSizeColor,
                                whiteSpace: 'nowrap', flexShrink: 0,
                            }}>{storageText}</span>

                            {/* Optimize Button — hujung kanan, berteks */}
                            <button
                                onClick={() => { if (onCleanupStorage) onCleanupStorage(); }}
                                disabled={isCleaningStorage}
                                title={isCleaningStorage ? 'Sedang membersih…' : 'Optimize — buang fail tidak terpakai'}
                                style={{
                                    ...S.optimizeBtn,
                                    color: storageAlert ? storageColor : '#a1a1aa',
                                    borderColor: storageAlert ? storageColor : 'rgba(255,255,255,0.10)',
                                    opacity: isCleaningStorage ? 0.5 : 1,
                                    cursor: isCleaningStorage ? 'not-allowed' : 'pointer',
                                }}
                                onMouseEnter={(e) => { if (!isCleaningStorage) e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            >
                                <span className="material-symbols-outlined" style={{
                                    fontSize: 13,
                                    ...(isCleaningStorage ? { animation: 'spin 1s linear infinite' } : {}),
                                }}>
                                    {isCleaningStorage ? 'refresh' : 'cleaning_services'}
                                </span>
                                <span style={{ fontSize: 10, fontWeight: 700 }}>
                                    {isCleaningStorage ? 'Cleaning' : 'Optimize'}
                                </span>
                            </button>
                        </div>

                        {/* Amaran — muncul HANYA bila kritikal (>90%) */}
                        {storageRatio > 0.9 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
                                <span className="material-symbols-outlined" style={{ fontSize: 11, color: '#ef4444', animation: 'pulse 2s infinite' }}>warning</span>
                                <span style={{ fontSize: 9, fontWeight: 700, color: '#ef4444' }}>
                                    {storageRatio >= 1 ? 'Storage full — please optimize' : 'Almost full!'}
                                </span>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ─── Footer: SATU baris profil — avatar · nama/plan · Settings · Log keluar ───
                 Baris "Settings" berasingan DIBUANG (digabung sebagai ikon di baris ini)
                 supaya bahagian bawah sidebar tidak bertingkat-tingkat. */}
            <div style={{ ...S.profileSection, padding: isCollapsed ? '12px 8px' : '8px 16px' }}>
                {/* Baris profil — paparan + dua butang tindakan di hujung kanan */}
                <div
                    style={{
                        ...S.profileRow,
                        flexDirection: isCollapsed ? 'column' : 'row',
                        justifyContent: isCollapsed ? 'center' : 'flex-start',
                        gap: isCollapsed ? 8 : 10,
                        padding: isCollapsed ? '8px 0' : '8px 8px',
                    }}
                >
                    {/* Avatar (kiri) — gambar Google diutamakan, fallback initial */}
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                        {userPhoto ? (
                            <img src={userPhoto} alt="" style={S.profileAvatar} referrerPolicy="no-referrer" />
                        ) : (
                            <div style={S.profileAvatarFallback}>
                                {userInitial}
                            </div>
                        )}
                        <div style={S.profileOnlineDot} />
                    </div>

                    {!isCollapsed && (
                        /* Nama + Plan (tengah) */
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#e4e4e7', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userName}</span>
                            <span style={{
                                fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1.3,
                                color: planColor,
                            }}>{planLabel}</span>
                        </div>
                    )}

                    {/* Butang Settings (kanan) — pautan sebenar supaya boleh dibuka
                        dalam tab baharu, sama seperti baris menu lain */}
                    <Link
                        to="/profile"
                        title="Settings"
                        aria-label="Settings"
                        style={{ ...S.profileIconBtn, textDecoration: 'none' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#e4e4e7'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#71717a'; }}
                    >
                        <span className="material-symbols-outlined" style={{ fontSize: 19 }}>settings</span>
                    </Link>

                    {/* Butang log keluar (hujung kanan) */}
                    <button
                        onClick={handleLogout}
                        title="Log keluar"
                        aria-label="Log keluar"
                        style={S.profileIconBtn}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.12)'; e.currentTarget.style.color = '#ef4444'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#71717a'; }}
                    >
                        <span className="material-symbols-outlined" style={{ fontSize: 19 }}>logout</span>
                    </button>
                </div>
            </div>

            {/* ─── Injected Styles ─── */}
            <style>{`
                .space-settings-btn {
                    opacity: 0;
                    transition: opacity 0.2s;
                }
                a:hover .space-settings-btn {
                    opacity: 1;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
            `}</style>
        </aside>
    );
};

/* ═══════════════════════════════════════════════════
   STYLES — Dark NavRail Theme
   ═══════════════════════════════════════════════════ */
const S = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        background: '#18181b',
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        /* ⛔ JANGAN kembalikan kepada `overflow: 'hidden'`.
           Senarai menu (<nav>) bertanda flexShrink:0, jadi bila jumlah item
           melebihi tinggi skrin — akaun admin ada ~18 item + submenu, dan
           skrin pendek/telefon lagi cepat penuh — item bawah sekali (Admin,
           Kelola Affiliate, kotak Storage, baris profil & Log Keluar) akan
           TERPOTONG dan langsung tidak boleh dicapai kerana tiada satu pun
           bekas yang boleh di-scroll. Kena PC dan telefon.
           overflow-x kekal hidden supaya teks menu tidak terjulur keluar
           semasa animasi kuncup/kembang lebar. */
        overflowX: 'hidden',
        overflowY: 'auto',
        minHeight: 0,
        flexShrink: 0,
        zIndex: 40,
        position: 'relative',
        height: '100%',
    },
    // ── Logo ──
    logoArea: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        cursor: 'pointer',
        flexShrink: 0,
    },
    logoIcon: {
        width: 36, height: 36,
        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        borderRadius: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 4px 12px rgba(99,102,241,0.3)',
        flexShrink: 0,
    },
    logoText: {
        fontSize: 18, fontWeight: 800, color: '#ffffff', letterSpacing: '-0.02em',
    },
    collapseBtn: {
        flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 26, height: 26, borderRadius: 7,
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: '#52525b', transition: 'all 0.2s', padding: 0,
    },
    betaBadge: {
        fontSize: 8, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.08em',
        padding: '2px 6px', borderRadius: 5,
        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        color: '#ffffff', lineHeight: 1.2,
    },

    // ── Nav ──
    navSection: {
        display: 'flex', flexDirection: 'column', gap: 2,
        padding: '0 12px',
        flexShrink: 0,
    },
    navItem: {
        height: 40, borderRadius: 8,
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'transparent', border: 'none', cursor: 'pointer',
        position: 'relative', transition: 'all 0.15s',
        whiteSpace: 'nowrap', fontFamily: 'inherit',
        textAlign: 'left', width: '100%',
        color: 'inherit',
    },
    activeIndicator: {
        position: 'absolute', left: -12, top: '50%', transform: 'translateY(-50%)',
        width: 3, height: 20, background: '#818cf8', borderRadius: '0 3px 3px 0',
    },
    subItem: {
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 10px', borderRadius: 6,
        border: 'none', cursor: 'pointer',
        fontFamily: 'inherit', textAlign: 'left', width: '100%',
        transition: 'all 0.15s',
        background: 'transparent',
    },

    // ── Extension card (menu atas sekali, gaya berbeza dari nav biasa) ──
    extCard: {
        display: 'flex', alignItems: 'center', gap: 10,
        borderRadius: 12,
        border: '1px solid rgba(129,140,248,0.35)',
        textDecoration: 'none',
        transition: 'background 0.2s',
        cursor: 'pointer',
    },
    extIconBox: {
        width: 30, height: 30, borderRadius: 9,
        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
        boxShadow: '0 3px 10px rgba(99,102,241,0.35)',
    },

    // ── Divider ──
    divider: {
        height: 1, background: 'rgba(255,255,255,0.06)',
    },

    // ── Spaces ──
    spacesSection: {
        flex: 1, overflowY: 'auto', padding: '8px 12px',
    },
    spacesHeader: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 12px', marginBottom: 8,
    },
    spacesTitle: {
        fontSize: 10, fontWeight: 700, color: '#52525b',
        textTransform: 'uppercase', letterSpacing: '0.08em',
    },
    spacesAddBtn: {
        width: 24, height: 24, borderRadius: 6,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: '#52525b', transition: 'all 0.2s',
    },
    addSpaceInput: {
        width: '100%', padding: '8px 10px', borderRadius: 8,
        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
        color: '#e4e4e7', fontSize: 13, outline: 'none', fontFamily: 'inherit',
    },
    addSpaceConfirm: {
        flex: 1, padding: '6px 0', borderRadius: 6, border: 'none',
        background: '#6366f1', color: '#fff', fontSize: 11, fontWeight: 700,
        cursor: 'pointer', fontFamily: 'inherit',
    },
    addSpaceCancel: {
        flex: 1, padding: '6px 0', borderRadius: 6, border: 'none',
        background: 'rgba(255,255,255,0.08)', color: '#a1a1aa', fontSize: 11, fontWeight: 700,
        cursor: 'pointer', fontFamily: 'inherit',
    },
    spaceItem: {
        display: 'flex', alignItems: 'center',
        borderRadius: 10, transition: 'all 0.2s',
        border: '1px solid transparent', textDecoration: 'none',
        cursor: 'pointer',
    },
    spaceSettingsBtn: {
        width: 28, height: 28, borderRadius: 6,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: '#52525b', transition: 'all 0.2s',
    },

    // ── Storage ──
    storageSection: {
        borderTop: '1px solid rgba(255,255,255,0.06)',
        flexShrink: 0,
    },
    storageBarTrack: {
        width: '100%', height: 4, borderRadius: 2,
        background: 'rgba(255,255,255,0.08)',
        overflow: 'hidden',
    },
    storageBarFill: {
        height: '100%', borderRadius: 2,
        transition: 'width 0.5s ease-in-out',
    },
    optimizeBtn: {
        flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '3px 8px', borderRadius: 6,
        background: 'transparent',
        border: '1px solid rgba(255,255,255,0.10)',
        whiteSpace: 'nowrap',
        fontFamily: 'inherit', transition: 'all 0.2s',
    },
    storageIconBtn: {
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 32, height: 32, borderRadius: 8,
        background: 'transparent', border: 'none', padding: 0,
        transition: 'all 0.2s',
    },

    // ── Profile ──
    profileSection: {
        flexShrink: 0,
    },
    profileIconBtn: {
        flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 28, height: 28, borderRadius: 8,
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: '#71717a', transition: 'all 0.2s', padding: 0,
    },
    profileRow: {
        display: 'flex', alignItems: 'center',
        borderRadius: 10, cursor: 'default',
        transition: 'all 0.2s',
    },
    profileAvatar: {
        width: 36, height: 36, borderRadius: '50%', objectFit: 'cover',
        flexShrink: 0, border: '2px solid rgba(255,255,255,0.1)',
    },
    profileAvatarFallback: {
        width: 36, height: 36, borderRadius: '50%',
        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontWeight: 700, fontSize: 13,
        flexShrink: 0, border: '2px solid rgba(255,255,255,0.1)',
    },
    profileOnlineDot: {
        position: 'absolute', bottom: -1, right: -1,
        width: 10, height: 10, borderRadius: '50%',
        background: '#22c55e', border: '2px solid #18181b',
    },
};

export default NewSidebar;
